import { useCallback, useMemo } from 'react';
import moment from '@g4/moment-utc';
import {
  CALENDAR_VIEW,
  DEPARTING,
  LIST_VIEW,
  RETURNING,
} from '../../../constants';
import FlightCalendarContent from '../FlightCalendarContent';
import SubHeaderContent from '../components/SubHeaderContent';
import { withValue } from '@g4/automation-hooks';
import {
  FLIGHTS_CALENDAR_VIEW,
  NEXT_ARROW,
  PREVIOUS_ARROW,
} from '../../../../../../automationHooks.json';

const useCalendarView = ({
  leg,
  isRoundTrip,
  tabDates,
  handleViewToggle,
  setSelectedDate,
  setSelectedFlight,
  lowFares,
  loading,
  stadiumGame,
  setBackupTabDate,
  view,
}) => {
  const handleSelectDate = useCallback(
    data => {
      const date = data.date.format('YYYY-MM-DD');
      const isDateClicked = true;
      let middle = date;
      if (leg !== DEPARTING) {
        if (
          tabDates[DEPARTING].selected &&
          moment(date).diff(tabDates[DEPARTING].selected, 'days') < 6
        ) {
          middle = moment(tabDates[DEPARTING].selected)
            .add(3, 'days')
            .format('YYYY-MM-DD');
        }
      }
      setBackupTabDate(prev => ({
        ...prev,
        [leg]: middle,
      }));

      setSelectedDate({ selected: date, middle: middle });
      setSelectedFlight(null);
      handleViewToggle(LIST_VIEW, leg, isDateClicked);
    },
    [
      setSelectedDate,
      setSelectedFlight,
      handleViewToggle,
      leg,
      tabDates,
      setBackupTabDate,
    ]
  );

  // This should probably not affect the list-view in this way.
  // Consider reverting 'middle' when toggling back to list-view.
  const handleShiftMonth = useCallback(
    date => {
      if (view[leg] === CALENDAR_VIEW) {
        setSelectedDate({
          middle: moment(date)
            .startOf('month')
            .format('YYYY-MM-DD'),
        });
      }
    },
    [leg, setSelectedDate, view]
  );

  const hasRoundTripDiscount =
    lowFares && !!lowFares.find(lowFare => !!lowFare.fare?.strikethrough);

  const calendarContent = useMemo(
    () => ({
      ...FlightCalendarContent,
      ...(isRoundTrip &&
        hasRoundTripDiscount && {
          subHeaderContent: {
            component: SubHeaderContent,
            props: { leg },
          },
        }),
    }),
    [leg, isRoundTrip, hasRoundTripDiscount]
  );

  const calendarAutomationHooks = useMemo(
    () => ({
      calendarView: withValue(FLIGHTS_CALENDAR_VIEW.hook, leg),
      previousMonthArrow: withValue(PREVIOUS_ARROW.hook, leg),
      nextMonthArrow: withValue(NEXT_ARROW.hook, leg),
    }),
    [leg]
  );

  const calendarData = useMemo(
    () => ({
      lowFares,
      isRoundTrip,
      departureDate: tabDates[DEPARTING].selected,
      returnDate: tabDates[RETURNING].selected,
      direction: leg,
      stadiumGame,
      isLoading: loading,
    }),
    [loading, lowFares, isRoundTrip, tabDates, stadiumGame, leg]
  );

  return {
    calendarContent,
    calendarData,
    calendarAutomationHooks,
    handleSelectDate,
    handleShiftMonth,
  };
};

export default useCalendarView;
